import { z } from 'zod';
/** The conditions each kind of field offers, in the order a menu shows them. */
export const FILTER_OPERATORS = {
    enum: ['is', 'isNot'],
    text: ['contains', 'notContains', 'startsWith', 'endsWith', 'eq', 'neq', 'empty', 'notEmpty'],
    date: ['today', 'lastDays', 'olderDays', 'on', 'after', 'before', 'between'],
};
/** Conditions without a value: the text is (not) empty, the time is today. */
const VALUELESS = new Set(['empty', 'notEmpty', 'today']);
/** Spans counted back from the day the list is read, in days - a link keeps meaning „the last 7 days“. */
export const DAY_SPANS = new Set(['lastDays', 'olderDays']);
const Day = z.iso.date();
const ALL_OPERATORS = [...new Set(Object.values(FILTER_OPERATORS).flat())];
export const FilterRule = z.object({
    /** The field's key. */
    f: z.string().min(1).max(40),
    /** The condition. */
    o: z.enum(ALL_OPERATORS),
    /** The value: text, a day, two days, or a number of days - none for `empty`, `today`. */
    v: z.union([z.string().max(200), z.int().min(1).max(3650), z.tuple([Day, Day])]).optional(),
});
export const Combinator = z.enum(['and', 'or']);
export const FilterGroup = z.object({ c: Combinator, r: z.array(FilterRule).min(1).max(20) });
export const Filter = z.object({ c: Combinator, g: z.array(FilterGroup).min(1).max(10) });
/** Whether a rule makes sense for its field: a condition of its kind, a value of the right shape. */
function fits(rule, field) {
    if (!FILTER_OPERATORS[field.type].includes(rule.o))
        return false;
    const { o, v } = rule;
    if (VALUELESS.has(o))
        return v === undefined;
    if (DAY_SPANS.has(o))
        return typeof v === 'number';
    if (o === 'between')
        return Array.isArray(v) && v[0] <= v[1];
    if (field.type === 'date')
        return typeof v === 'string' && Day.safeParse(v).success;
    if (typeof v !== 'string' || v.trim() === '')
        return false;
    return field.type !== 'enum' || field.values === undefined || field.values.includes(v);
}
/**
 * The filter in `text` (the address's JSON), if it is one for these fields; otherwise `null` -
 * an unknown field or a value that does not fit is never silently dropped.
 */
export function readFilter(text, fields) {
    let json;
    try {
        json = JSON.parse(text);
    }
    catch {
        return null;
    }
    const parsed = Filter.safeParse(json);
    if (!parsed.success)
        return null;
    const known = new Map(fields.map((field) => [field.key, field]));
    const valid = parsed.data.g.every((group) => group.r.every((rule) => {
        const field = known.get(rule.f);
        return field !== undefined && fits(rule, field);
    }));
    return valid ? parsed.data : null;
}
/** The query parameter of a list with these fields: the filter as JSON. */
export const filterParam = (fields) => z
    .string()
    .max(4000)
    .refine((text) => readFilter(text, fields) !== null, 'a filter of this list as JSON')
    .meta({
    description: 'Rules in groups as JSON: `{"c":"and","g":[{"c":"or","r":[{"f":"<field>","o":"<condition>","v":<value>}]}]}`.',
});
//# sourceMappingURL=filters.js.map